import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, Product } from '@/types/product';

interface StoreState {
  // Cart
  cart: CartItem[];
  addToCart: (product: Product, colour: string, size?: string, quantity?: number) => void;
  removeFromCart: (productId: string, colour: string, size?: string) => void;
  updateQuantity: (productId: string, colour: string, size: string | undefined, quantity: number) => void;
  clearCart: () => void;
  cartTotal: () => number;
  cartCount: () => number;

  // Wishlist
  wishlist: string[];
  addToWishlist: (productId: string) => void;
  removeFromWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;

  // UI Mode
  aiMode: boolean;
  toggleAIMode: () => void;

  // Anonymous Profile
  anonymousProfileId: string | null;
  setAnonymousProfileId: (id: string) => void;

  // Toast notifications
  toasts: Array<{ id: string; message: string; type: 'success' | 'error' | 'info' }>;
  addToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  removeToast: (id: string) => void;
}

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      // Cart
      cart: [],
      addToCart: (product, colour, size, quantity = 1) => {
        const cart = get().cart;
        const existingIndex = cart.findIndex(
          item => item.product.id === product.id &&
                  item.colour === colour &&
                  item.size === size
        );

        if (existingIndex >= 0) {
          const updated = [...cart];
          updated[existingIndex].quantity += quantity;
          set({ cart: updated });
        } else {
          set({ cart: [...cart, { product, colour, size, quantity }] });
        }

        get().addToast(`Added ${product.name} to basket`, 'success');
      },

      removeFromCart: (productId, colour, size) => {
        set({
          cart: get().cart.filter(
            item => !(item.product.id === productId &&
                     item.colour === colour &&
                     item.size === size)
          )
        });
      },

      updateQuantity: (productId, colour, size, quantity) => {
        if (quantity <= 0) {
          get().removeFromCart(productId, colour, size);
          return;
        }

        const cart = get().cart;
        const index = cart.findIndex(
          item => item.product.id === productId &&
                  item.colour === colour &&
                  item.size === size
        );

        if (index >= 0) {
          const updated = [...cart];
          updated[index].quantity = quantity;
          set({ cart: updated });
        }
      },

      clearCart: () => set({ cart: [] }),

      cartTotal: () => {
        return get().cart.reduce((total, item) => {
          const price = item.product.salePrice || item.product.price;
          return total + (price * item.quantity);
        }, 0);
      },

      cartCount: () => {
        return get().cart.reduce((count, item) => count + item.quantity, 0);
      },

      // Wishlist
      wishlist: [],
      addToWishlist: (productId) => {
        if (!get().wishlist.includes(productId)) {
          set({ wishlist: [...get().wishlist, productId] });
          get().addToast('Added to wishlist', 'success');
        }
      },

      removeFromWishlist: (productId) => {
        set({ wishlist: get().wishlist.filter(id => id !== productId) });
      },

      isInWishlist: (productId) => get().wishlist.includes(productId),

      // AI Mode
      aiMode: false,
      toggleAIMode: () => set({ aiMode: !get().aiMode }),

      // Anonymous Profile
      anonymousProfileId: null,
      setAnonymousProfileId: (id) => set({ anonymousProfileId: id }),

      // Toasts
      toasts: [],
      addToast: (message, type = 'success') => {
        const id = Math.random().toString(36).substr(2, 9);
        set({ toasts: [...get().toasts, { id, message, type }] });

        setTimeout(() => {
          get().removeToast(id);
        }, 3000);
      },

      removeToast: (id) => {
        set({ toasts: get().toasts.filter(t => t.id !== id) });
      },
    }),
    {
      name: 'peakpath-storage',
      partialize: (state) => ({
        cart: state.cart,
        wishlist: state.wishlist,
        aiMode: state.aiMode,
        anonymousProfileId: state.anonymousProfileId,
      }),
    }
  )
);
